import React from 'react';
import Logo from '../../components/Logo';
import PublicNav from '../../components/PublicNav';
import { useAuth } from '../../context/AuthContext';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Seo } from '../../components/Seo';
import guidesData from '../../content/guides.generated.json';
import GuidePoll from './GuidePoll';
import AuthorCard from './AuthorCard';
import GuideShareRow from './GuideShareRow';

const BASE_URL = 'https://verifaeye.com.au';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function GuideArticle() {
  const { isAuthenticated } = useAuth();
  const { slug } = useParams();
  const guides = guidesData.guides || [];
  const guide = guides.find((g) => g.slug === slug);

  if (!guide) return <Navigate to="/guides" replace />;

  const related = guides.filter((g) => g.slug !== slug).slice(0, 3);
  const canonical = `${BASE_URL}/guides/${guide.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.date,
    dateModified: guide.updated || guide.date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    author: {
      '@type': 'Person',
      name: guide.author?.name || 'VerifAEye',
      ...(guide.author?.description ? { description: guide.author.description } : {}),
      ...(guide.author?.linkedin ? { sameAs: [guide.author.linkedin] } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: 'Symbionic',
      url: 'https://symbionic.com.au',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${BASE_URL}/guides` },
      { '@type': 'ListItem', position: 3, name: guide.title, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="guide-article-page">
      <Seo
        title={`${guide.title} | VerifAEye`}
        description={guide.description}
        path={`/guides/${guide.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <PublicNav isAuthenticated={isAuthenticated} current="/guides" />

      <article className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm text-slate-500">
            <Link to="/guides" className="hover:text-slate-700">Guides</Link> · {guide.readTime}
          </p>
          <h1
            className="mt-4 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            data-testid="guide-title"
          >
            {guide.title}
          </h1>

          <div className="mt-8 border-y border-slate-200 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
            <div>
              <p className="font-medium text-slate-900" data-testid="guide-author-name">
                {guide.author?.name}
              </p>
              {guide.author?.description && (
                <p className="text-slate-500 text-xs mt-0.5">{guide.author.description}</p>
              )}
            </div>
            <div className="ml-auto text-right">
              <p>
                Published <time dateTime={guide.date}>{formatDate(guide.date)}</time>
              </p>
              {guide.updated && guide.updated !== guide.date && (
                <p className="text-slate-500 text-xs mt-0.5">
                  Updated <time dateTime={guide.updated}>{formatDate(guide.updated)}</time>
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <GuideShareRow slug={guide.slug} title={guide.title} />
          </div>

          <div
            className="guide-body prose prose-slate max-w-none mt-10"
            data-testid="guide-body"
            dangerouslySetInnerHTML={{ __html: guide.html }}
          />

          <div className="mt-10 pt-6 border-t border-slate-100">
            <GuideShareRow slug={guide.slug} title={guide.title} />
          </div>

          {related.length > 0 && (
            <section className="mt-20 border-t border-slate-200 pt-10" data-testid="guide-related">
              <h2 className="text-2xl font-semibold text-slate-900">Related guides</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {related.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guides/${g.slug}`}
                    data-testid={`related-${g.slug}`}
                    className="block rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition"
                  >
                    <p className="text-xs uppercase tracking-wider text-slate-400">{g.readTime}</p>
                    <p className="mt-2 font-semibold text-slate-900 text-sm leading-snug">{g.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <AuthorCard author={guide.author} />

          <GuidePoll slug={guide.slug} />

          <section className="mt-16 rounded-2xl bg-slate-900 text-white p-10 text-center" data-testid="guide-cta">
            <h2 className="text-3xl font-semibold">Document it before you need it.</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">
              Start your bionic home inventory today — free to begin, and your evidence stays in your own Google Drive.
            </p>
            <Link
              to="/register"
              data-testid="guide-cta-register"
              className="inline-block mt-6 bg-white text-slate-900 px-6 py-3 rounded-md font-semibold hover:bg-slate-100 transition"
            >
              Create your free account
            </Link>
          </section>
        </div>
      </article>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-slate-600 text-sm ml-2">— The Proof is in the Detail</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/guides" className="text-slate-500 hover:text-slate-700">Guides</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-700">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
